import { useEffect, useState } from "react";

import {
  faArrowRight,
  faCalendar,
  faCamera,
  faCircleExclamation,
  faClock,
  faCloudUploadAlt,
  faFolderOpen,
  faImages,
  faLocation,
  faMobileScreenButton,
  faSpinner,
  faTimes,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams } from "react-router-dom";

import Header from "../components/Header/Header";
import SEOHead from "../components/SEOHead/SEOHead";
import ToastContainer from "../components/Toast/ToastContainer";
import { useLanguage } from "../contexts/LanguageContext";
import { useModalScrollLock } from "../hooks/useModalScrollLock";
import useToast from "../hooks/useToast";
import apiService from "../services/apiService";
import { formatDisplayDate, formatTimestampDate } from "../utils/dateUtils";

import "./EventView.css";

const EventView = () => {
  const { eventId } = useParams();
  const { t, translations } = useLanguage();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [totalUploaders, setTotalUploaders] = useState(0);

  // New states for photo management
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploaderName, setUploaderName] = useState(() => {
    return localStorage.getItem("eventUploaderName") || "";
  });
  const [showNamePrompt, setShowNamePrompt] = useState(false);

  // Toast hook
  const { toasts, showSuccess, showError, removeToast } = useToast();

  useEffect(() => {
    const loadEvent = async () => {
      if (!eventId) {
        setError("Event ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const eventData = await apiService.getEvent(eventId);

        setTotalUploaders(eventData?.totalUsersUploaded || 0);

        // Transform the API response to match the expected format
        const transformedEvent = {
          id: eventData.id,
          name: eventData.name,
          date: eventData.date,
          time: eventData.time,
          description: eventData.description,
          location: eventData.location,
          host: eventData.hostName,
          hostEmail: eventData.hostEmail,
          totalPhotos: eventData.stats?.totalPhotos || 0,
          isActive: true,
          qrCode: eventData.qrCode,
          shareUrl: eventData.shareUrl,
          createdAt: eventData.createdAt,
        };

        setEvent(transformedEvent);

        try {
          const photoResponse = await apiService.getEventPhotos(eventId, 0, 8);
          if (photoResponse?.success) {
            setPhotos(photoResponse.photos || []);
          }
        } catch (photoError) {
          console.error("Error loading event photo preview:", photoError);
        }
      } catch (error) {
        console.error("Error loading event:", error);
        setError(error.message || "Failed to load event");
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [eventId]);

  const handleFileUpload = async (files) => {
    // Check if we need to ask for name
    if (!uploaderName.trim()) {
      setShowNamePrompt(true);
      // Store files temporarily
      window.tempFiles = files;
      return;
    }

    try {
      // Add files to pending photos instead of uploading immediately
      for (let file of files) {
        if (file.type.startsWith("image/")) {
          const metadata = await getImageMetadata(file);
          const tempId = Date.now() + Math.random();

          const pendingPhoto = {
            id: tempId,
            file: file,
            url: URL.createObjectURL(file),
            thumbnail: URL.createObjectURL(file),
            metadata: metadata,
            uploadedBy: uploaderName.trim(),
            uploadDate: new Date().toISOString(),
            isPending: true,
          };

          setPendingPhotos((prev) => [...prev, pendingPhoto]);
        }
      }

      showSuccess(`${files.length} ${t("eventViewUpload.text1")}`);
    } catch (error) {
      showError(t("eventViewUpload.text2"));
    }
  };

  const handleNameSubmit = () => {
    if (!uploaderName.trim()) {
      showError(t("eventViewUpload.text3"));
      return;
    }

    if (localStorage.getItem("eventUploaderName")) {
      showSuccess(t("eventViewUpload.text7"));
    }

    localStorage.setItem("eventUploaderName", uploaderName.trim());
    setShowNamePrompt(false);

    // Process temporarily stored files
    if (window.tempFiles) {
      handleFileUpload(window.tempFiles);
      window.tempFiles = null;
    }
  };

  const removePendingPhoto = (photoId) => {
    setPendingPhotos((prev) => {
      const photoToRemove = prev.find((p) => p.id === photoId);
      if (photoToRemove && photoToRemove.url) {
        URL.revokeObjectURL(photoToRemove.url);
      }
      return prev.filter((p) => p.id !== photoId);
    });
  };

  const uploadAllPhotos = async () => {
    if (pendingPhotos.length === 0) return;

    setUploadingPhotos(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (let pendingPhoto of pendingPhotos) {
        try {
          const result = await apiService.uploadEventPhoto(
            eventId,
            pendingPhoto.file,
            {
              ...pendingPhoto.metadata,
              uploaderName: pendingPhoto.uploadedBy,
            }
          );

          if (result.success) {
            const newPhoto = {
              id: result.photo.id,
              url: result.photo.url,
              thumbnail: result.photo.url,
              uploadedBy: pendingPhoto.uploadedBy,
              uploadDate: result.photo.created,
            };

            setPhotos((prev) => [newPhoto, ...prev]);
            successCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }

      // Clear pending photos and show results
      setPendingPhotos([]);

      if (successCount > 0) {
        showSuccess(`${successCount} ${t("eventViewUpload.text4")}`);
      }
      if (errorCount > 0) {
        showError(`${errorCount} ${t("eventViewUpload.text2")}`);
      }
    } catch (error) {
      showError(t("eventViewUpload.text2"));
    } finally {
      setUploadingPhotos(false);
    }
  };

  const getImageMetadata = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          device: navigator.userAgent,
          orientation: img.width > img.height ? "landscape" : "portrait",
        });
      };
      img.onerror = () => resolve({});
      img.src = URL.createObjectURL(file);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  // Native camera functions
  const openCamera = () => {
    // Always use native file input with camera capture
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.capture = "environment"; // Use rear camera by default
    fileInput.multiple = false; // Single photo for camera

    fileInput.onchange = (e) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFileUpload(Array.from(e.target.files));
      }
    };

    fileInput.click();
  };

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent;
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        );
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Lock scroll if name prompt modal is open
  useModalScrollLock(showNamePrompt);

  if (loading) {
    return (
      <div className="event-view-page">
        <Header fullHeader={false} showLanguageSelector />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("commons.loadingEvent")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-view">
        <div className="event-view-page">
          <Header fullHeader={false} showLanguageSelector />
          <div className="error-container">
            <div className="error-message">
              <h2><FontAwesomeIcon icon={faCircleExclamation} /> {t("eventView.errorLoadingEvent")}</h2>
              <p>{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="retry-button"
              >
                {t("commons.tryAgain")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-view">
        <div className="event-view-page">
          <Header fullHeader={false} showLanguageSelector />
          <div className="error-container">
            <div className="error-message">
              <h2><FontAwesomeIcon icon={faCalendar} /> {t("eventView.eventNotFound")}</h2>
              <p>{t("eventView.eventNotFoundMessage")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If not mobile, show desktop message
  if (!isMobile && !loading) {
    return (
      <div className="event-view event-view-page">
        <title>{t("pagesTitle.eventView")}</title>
        <Header fullHeader={false} showLanguageSelector />
        <div className="desktop-only-message">
          <div className="desktop-message-container">
            <div className="desktop-message-content">
              <h2><FontAwesomeIcon icon={faMobileScreenButton} /> {t("eventView.mobileExperience")}</h2>
              <p>{t("eventView.mobileExperienceMessage")}</p>
              <div className="desktop-message-qr">
                {event?.qrCode && (
                  <>
                    <p>
                      <strong>{t("eventView.scanQRCode")}</strong>
                    </p>
                    <img
                      src={event.qrCode}
                      alt="QR Code"
                      className="desktop-qr-code"
                    />
                  </>
                )}
              </div>
              <div className="desktop-message-url">
                <p>
                  <strong>{t("eventView.copyLink")}</strong>
                </p>
                <div className="url-copy-container">
                  <input
                    type="text"
                    value={window.location.href}
                    readOnly
                    className="url-input"
                  />
                  <button
                    className="copy-url-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                    }}
                  >
                    {t("commons.copy")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SEO Data
  const eventTitle = event
    ? translations?.seo?.eventView?.title?.replace("{eventName}", event.name) ||
      `Evento: ${event.name} - Galería de Fotos | Snappin`
    : "Galería de Fotos del Evento | Snappin";

  const eventDescription = event
    ? translations?.seo?.eventView?.description?.replace(
        "{eventName}",
        event.name
      ) ||
      `Explora y comparte fotos del evento ${event.name}. Sube tus mejores momentos y descarga todas las fotos de este evento especial.`
    : "Explora y comparte fotos de eventos especiales en Snappin";

  const eventSchema = event
    ? {
        "@context": "https://schema.org",
        "@type": "Event",
        name: event.name,
        description: event.description || eventDescription,
        startDate: event.date,
        location: {
          "@type": "Place",
          name: event.location || "Ubicación del evento",
        },
        organizer: {
          "@type": "Organization",
          name: "Snappin",
        },
        url: window.location.href,
        image:
          photos.length > 0
            ? photos[0].url
            : `${window.location.origin}/logo.png`,
      }
    : null;

  return (
    <div className="event-view-page">
      <SEOHead
        title={eventTitle}
        description={eventDescription}
        type="article"
        image={photos.length > 0 ? photos[0].url : undefined}
        schema={eventSchema}
      />
      <Header fullHeader={false} showLanguageSelector />
      <div className="event-view">
        {/* Event Header */}
        <div className="event-header">
          <div className="event-info">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
            >
              <h1>{event.name}</h1>
              {event?.totalPhotos > 0 && (
                <div
                  className="event-stats"
                  style={{
                    display: "flex",
                    gap: "1rem",
                    margin: "10px",
                    alignItems: "center",
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <a
                    href={`/event/${event.id}/photos`}
                    className="view-photos-link"
                  >
                    <span className="event-preview-thumbs">
                      {photos.slice(0, 3).map((photo) => (
                        <img key={photo.id} src={photo.url || photo.thumbnail} alt="" />
                      ))}
                    </span>
                    <span className="event-preview-copy">
                      <strong>{event.totalPhotos}</strong>
                      <span>{t("eventView.eventViewAllPhotos")}</span>
                    </span>
                    <FontAwesomeIcon icon={faArrowRight} className="event-preview-arrow" />
                  </a>
                </div>
              )}
            </div>
            <div className="event-meta">
              <span>
                <FontAwesomeIcon icon={faCalendar} />{" "}
                {formatDisplayDate(event.date)}
              </span>
              <span>
                <FontAwesomeIcon icon={faClock} /> {event.time}
              </span>
              <span>
                <FontAwesomeIcon icon={faLocation} /> {event.location}
              </span>
              <span>
                <FontAwesomeIcon icon={faUser} /> {t("eventView.hosted")}{" "}
                {event.host}
              </span>
            </div>
          </div>
        </div>
        <div className="container">
          {/* Total Uploaders Message */}
          {totalUploaders > 0 && (
            <div className="uploaders-info-banner">
              <div className="uploaders-info-content">
                <FontAwesomeIcon icon={faUser} className="uploaders-icon" />
                <span className="uploaders-text">
                  {totalUploaders === 1
                    ? `${totalUploaders} ${t("eventView.personSharing")}`
                    : `${totalUploaders} ${t("eventView.peoplesSharing")}`}
                </span>
              </div>
            </div>
          )}

          {/* Name Prompt Modal */}
          {showNamePrompt && (
            <div className="name-prompt-overlay">
              <div className="name-prompt-modal">
                <div className="name-prompt-header">
                  <h3>{t("eventView.guestName")}</h3>
                  <p>{t("eventView.guestNameDescription")}</p>
                </div>
                <div className="name-prompt-form">
                  <input
                    type="text"
                    placeholder={t("eventView.guestNamePlaceholder")}
                    value={uploaderName}
                    onChange={(e) => setUploaderName(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleNameSubmit()}
                    autoFocus
                    maxLength={50}
                  />
                  <div className="name-prompt-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowNamePrompt(false);
                        window.tempFiles = null;
                      }}
                    >
                      {t("commons.cancel")}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleNameSubmit}
                    >
                      {t("commons.continue")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upload Section */}
          <div className="upload-section">
            <div className="upload-section-header">
              <h2>
                <FontAwesomeIcon
                  icon={faCamera}
                  className="gradient-color"
                  style={{ marginRight: "8px" }}
                />
                {t("eventView.shareYourPhotos")}
              </h2>
              {uploaderName && (
                <div className="uploader-info">
                  <span>
                    {t("eventView.uploadingAs")}:{" "}
                    <strong>{uploaderName}</strong>
                  </span>
                  <button
                    className="change-name-btn"
                    onClick={() => setShowNamePrompt(true)}
                  >
                    {t("commons.change")}
                  </button>
                </div>
              )}
            </div>

            {/* Desktop Upload */}
            <div className="desktop-upload">
              <div
                className={`upload-area ${dragActive ? "drag-active" : ""}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="upload-content">
                  <div className="upload-icon"><FontAwesomeIcon icon={faCloudUploadAlt} /></div>
                  <h3>{t("eventView.dragAndDrop")}</h3>
                  <p>{t("eventView.dragAndDropOr")}</p>
                  <div className="upload-buttons">
                    <label className="upload-btn btn btn-primary">
                      <FontAwesomeIcon icon={faFolderOpen} /> {t("eventView.chooseFile")}
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileInput}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                  <p className="upload-hint">{t("eventView.fileSupport")}</p>
                </div>
              </div>
            </div>

            {/* Mobile Upload */}
            <div className="mobile-upload">
              <div className="mobile-upload-container">
                <div className="mobile-upload-header">
                  <h3>{t("eventView.addPhotos")}</h3>
                  <p>{t("eventView.chooseHowToShare")}</p>
                </div>
                <div className="mobile-upload-buttons">
                  <button
                    className="mobile-upload-btn mobile-camera-btn"
                    onClick={openCamera}
                  >
                    <FontAwesomeIcon icon={faCamera} />
                    {}
                    {t("eventView.takePhoto")}
                  </button>
                  <label className="mobile-upload-btn mobile-files-btn">
                    <FontAwesomeIcon icon={faImages} />
                    {t("eventView.chooseFromGallery")}
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileInput}
                      style={{ display: "none" }}
                    />
                  </label>
                </div>
                <p className="mobile-upload-hint">
                  {t("eventView.fileSupport")}
                </p>
              </div>
            </div>

            {/* Pending Photos Section */}
            {pendingPhotos.length > 0 && (
              <div className="pending-photos-section">
                <div className="pending-photos-header">
                  <h3>
                    {t("eventView.photosReady")} ({pendingPhotos.length})
                  </h3>
                </div>

                <div className="pending-photos-grid">
                  {pendingPhotos.map((photo) => (
                    <div key={photo.id} className="pending-photo-item">
                      <div className="pending-photo-card">
                        <img src={photo.thumbnail} alt="Pending upload" />
                        <div className="pending-photo-overlay">
                          <button
                            className="remove-pending-btn"
                            onClick={() => removePendingPhoto(photo.id)}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                        <div className="pending-photo-info">
                          <span className="pending-badge">
                            {t("commons.pending")}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pending-photos-actions">
                  <button
                    className="btn btn-primary"
                    onClick={uploadAllPhotos}
                    disabled={uploadingPhotos}
                  >
                    {uploadingPhotos ? (
                      <span style={{ fontWeight: "bold" }}>
                        <FontAwesomeIcon
                          style={{ marginRight: "0.5rem" }}
                          icon={faSpinner}
                          spin
                        />
                        {t("commons.uploading")}
                      </span>
                    ) : (
                      <span style={{ fontWeight: "bold" }}>
                        <FontAwesomeIcon
                          style={{ marginRight: "0.5rem" }}
                          icon={faCloudUploadAlt}
                        />
                        {t("commons.uploadAllPhotos")}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div className="progress-fill"></div>
                </div>
                <p>{t("commons.uploadingPhotos")}</p>
              </div>
            )}
          </div>

          {/* Photos Gallery */}
          {photos.length > 0 && (
            <div className="photos-gallery">
              <div className="gallery-header">
                <h2>
                  <FontAwesomeIcon icon={faCamera} />{" "}
                  {t("eventView.eventPhotos")} ({photos.length})
                </h2>
                <p>{t("eventView.seeAllMoments")}</p>
              </div>
              <div className="photos-grid">
                {photos.map((photo) => (
                  <div key={photo.id} className="photo-item">
                    <div className="photo-card">
                      <img src={photo.thumbnail} alt={`Photo ${photo.id}`} />
                      <div className="photo-info">
                        <span className="photo-uploader">
                          {uploaderName || t("commons.anonymous")}
                        </span>
                        <span className="photo-date">
                          {formatTimestampDate(photo.uploadDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default EventView;
